'use client';

import React, { useState, useEffect, useCallback } from 'react';
import FavoriteButton from './FavoriteButton';
import { useLoading } from '@/context/LoadingContext';
import { loadingKey, loadingKeys } from '@/utils/loadingKeys';
import log from '@/utils/logger';
import debounce from 'lodash.debounce';
import { addEntryToShortlist, removeEntryFromShortlist } from '@/api/requests/shortlistEntries';

interface FavoriteButtonContainerProps {
  tenderId: string;
  shortlistId?: string | null;
  initEntryId?: string | null;
  onRemoveSuccess?: () => void;
}

const FavoriteButtonContainer: React.FC<FavoriteButtonContainerProps> = ({
  tenderId,
  shortlistId,
  initEntryId,
  onRemoveSuccess,
}) => {
  const { isLoading, setLoading } = useLoading();
  const [entryId, setEntryId] = useState<string | null>(initEntryId || null);

  const featureKey = loadingKey(loadingKeys.FAVORITE_BUTTON, tenderId);

  useEffect(() => {
    setEntryId(initEntryId || null);
  }, [initEntryId]);

  const toggleFavorite = async () => {
    setLoading(featureKey, true);

    let didRemove = false;
    try {
      if (entryId) {
        // remove
        await removeEntryFromShortlist(entryId);
        setEntryId(null);
        didRemove = true;
      } else {
        // add
        if (!shortlistId) return;
        const resp = await addEntryToShortlist(shortlistId, tenderId);
        setEntryId(resp.id);
      }
    } catch (err) {
      log.error('Error toggling favorite:', err);
    } finally {
      // clear loading *before* asking parent to unmount
      setLoading(featureKey, false);
    }

    // only after loading has cleared do we remove the card from the list
    if (didRemove) {
      onRemoveSuccess?.();
    }
  };
  const debouncedToggleFavorite = useCallback(debounce(toggleFavorite, 50), [toggleFavorite]);

  return (
    <FavoriteButton
      isFavorite={!!entryId}
      disabled={isLoading(featureKey) || isLoading(loadingKeys.FETCH_TENDER_DATA)}
      onFavoriteToggle={debouncedToggleFavorite}
    />
  );
};

export default FavoriteButtonContainer;
